import { load, type CheerioAPI } from 'cheerio/slim';

import { treasurerSourceUrl } from '../../../src/source-links';

/**
 * Official secured tax bills from the San Francisco Treasurer & Tax Collector
 * (TaxSys / GovHub portal). The portal serves its account and bill pages inside
 * an iframe on county-taxes.net behind a browser challenge, so this module only
 * parses the HTML a browser session delivers; it never talks to the portal
 * directly. Everything here is an official record: amounts, statuses, and paid
 * dates come from the Tax Collector, never from estimates or workspace input.
 */

export const TREASURER_PARSER_VERSION = 4;

export type TaxBillKind =
  | 'annual'
  | 'supplemental'
  | 'escape'
  | 'paymentPlan'
  /** Prior-year taxes in default; the portal bills the redemption amount as one item. */
  | 'defaulted'
  | 'other';

export type TaxInstallmentStatus = 'paid' | 'unpaid' | 'unknown';

export type TaxInstallment = {
  /** "1st Installment", "2nd Installment", or the plan label shown by the portal. */
  label: string;
  /** Balance still owed on this installment. */
  amountDue: number | null;
  status: TaxInstallmentStatus;
  /** Amount the Tax Collector shows as paid. */
  paidAmount: number | null;
  /** Date the Tax Collector posted the payment (MM/DD/YYYY as published). */
  paidOn: string | null;
  /** Delinquency date, or the "valid through" date for a payment plan amount. */
  delinquentAfter: string | null;
};

export type TaxAssessedValues = {
  land: number | null;
  improvements: number | null;
  personalProperty: number | null;
  exemptions: number | null;
  net: number | null;
};

export type TaxBillDetail = {
  /** Official payment schedule; absent on older cached snapshots. */
  paymentSchedule?: TaxInstallment[];
  fiscalYear: string | null;
  assessed: TaxAssessedValues | null;
  /** Prior and change values are only present on supplemental and escape bills. */
  prior: TaxAssessedValues | null;
  change: TaxAssessedValues | null;
  assessmentYear: string | null;
  assessmentEvent: string | null;
  effectiveDate: string | null;
  billablePeriod: string | null;
  issuedOn: string | null;
  totalTax: number | null;
  taxRate: string | null;
  daysOfOwnership: string | null;
  /** Every label/value pair the portal shows, grouped by its section header. */
  sections: Record<string, Record<string, string>>;
};

export type TaxBill = {
  billNumber: string;
  /** Roll year shown by the portal (2025 = FY 2025–26 for annual bills). */
  year: number | null;
  kind: TaxBillKind;
  /** Portal label, e.g. "2025 Secured Escape Bill #20250474600". */
  label: string;
  /** Bill page a person can open in their own browser. */
  detailUrl: string;
  /** The iframe document behind `detailUrl`; what a browser session must load. */
  frameUrl: string;
  /** Official PDF endpoint, when the portal offers one for this bill. */
  printUrl: string | null;
  /** Bill number of the payment plan that covers this bill, if any. */
  paymentPlanBillNumber: string | null;
  installments: TaxInstallment[];
  detail: TaxBillDetail | null;
};

export type TaxOwnershipPeriod = {
  /** Portal label, e.g. "Current Owner (October 13, 2023 – Present)". */
  label: string;
  current: boolean;
  bills: TaxBill[];
};

export type TaxAccount = {
  accountNumber: string;
  address: string;
  /** Portal account page (the iframe document that renders the account). */
  accountUrl: string;
  totalAmountDue: number | null;
  periods: TaxOwnershipPeriod[];
};

export type TaxBillSnapshot = {
  parserVersion: number;
  parcelNumber: string;
  fetchedAt: string;
  /** Search URL a person can open; the portal itself requires a browser. */
  sourceUrl: string;
  account: TaxAccount | null;
  /** Bills whose detail page could not be loaded in this run. */
  detailFailures: string[];
  /** Detail reads still owed after a bounded pass, including retained live plans. */
  pendingDetailBillNumbers?: string[];
};

export class TreasurerParseError extends Error {
  constructor(
    message: string,
    readonly providerStatus?: number,
  ) {
    super(message);
    this.name = 'TreasurerParseError';
  }
}

const text = (value: string) =>
  value
    .replace(/\u200b|\u200c|\u200d|\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function parseMoney(value: string | null | undefined) {
  if (!value) return null;
  const match = /-?\$?\s*-?[\d,]+(?:\.\d+)?/.exec(value.replace(/\u00a0/g, ' '));
  if (!match) return null;
  const negative = /-|\(/.test(match[0]) || value.trim().startsWith('(');
  const amount = Number(match[0].replace(/[^\d.]/g, ''));
  if (!Number.isFinite(amount)) return null;
  return negative ? -amount : amount;
}

const parseDate = (value: string | null | undefined) => {
  const match = /\b(\d{2}\/\d{2}\/\d{4})\b/.exec(value ?? '');
  return match ? match[1]! : null;
};

export function classifyTaxBill(label: string): TaxBillKind {
  const lower = label.toLowerCase();
  if (lower.includes('payment plan')) return 'paymentPlan';
  if (lower.includes('default')) return 'defaulted';
  if (lower.includes('supplemental')) return 'supplemental';
  if (lower.includes('escape')) return 'escape';
  if (lower.includes('annual')) return 'annual';
  return 'other';
}

/** "No Balance Due" is how the portal marks a settled installment with nothing owed. */
function installmentStatus(label: string): TaxInstallmentStatus {
  if (label === 'paid' || label === 'no balance due') return 'paid';
  return label === 'unpaid' ? 'unpaid' : 'unknown';
}

function billNumberFrom(label: string) {
  const match = /#\s*(\d{6,})/.exec(label);
  return match ? match[1]! : null;
}

function billYearFrom(label: string) {
  const match = /^\s*(\d{4})\b/.exec(label);
  return match ? Number(match[1]) : null;
}

function absolute(href: string | undefined, base: string) {
  if (!href) return null;
  try {
    return new URL(href, base).toString();
  } catch {
    return null;
  }
}

/** Parses the account summary document (the `/iframe-taxsys/.../property-tax/<account>` page). */
export function parseTreasurerAccountPage(html: string, accountUrl: string): TaxAccount | null {
  const $ = load(html);
  const title = text($('title').text());
  const heading = text($('.account-header h1').first().text());
  const accountMatch = /^Account\s+(\d{4}[A-Z]?-\d{3}[A-Z]?)(?=\s|$)/i.exec(heading);
  if (!accountMatch) {
    if (/Account Summary/i.test(title))
      throw new TreasurerParseError('Treasurer account page did not include an account header.');
    return null;
  }
  const accountNumber = accountMatch[1]!;
  if ($('table.bills').length === 0)
    throw new TreasurerParseError('Treasurer account page has no bill history.');
  const address = text($('.account-header .account-detail .value').first().text());
  const frameBase = accountUrl.split('/property-tax/')[0]!;
  const frameUrlFor = (link: ReturnType<CheerioAPI>) => {
    const navigate = /iframe_navigate\('([^']+)'/.exec(link.attr('onclick') ?? '');
    return navigate ? `${frameBase}${navigate[1]!.split('#')[0]}` : null;
  };

  // Installment rows and the paid-status cells only exist in the desktop layout.
  const parseInstallmentRows = (rows: ReturnType<CheerioAPI>): TaxInstallment[] => {
    const installments: TaxInstallment[] = [];
    rows.each((_, row) => {
      const cells = $(row);
      const label = text(cells.find('th.description').text());
      if (!label) return;
      const statusCell = cells.find('td.status');
      const statusLabel = text(statusCell.find('.label').text()).toLowerCase();
      const asOf = text(cells.find('td.as-of').text());
      const paid = statusLabel === 'paid';
      if (
        paid &&
        (!parseDate(asOf) || parseMoney(text(statusCell.find('span:not(.label)').text())) === null)
      )
        throw new TreasurerParseError('Treasurer payment details are incomplete.');
      installments.push({
        label,
        amountDue: parseMoney(text(cells.find('td.balance').text())),
        status: installmentStatus(statusLabel),
        paidAmount: paid ? parseMoney(text(statusCell.find('span:not(.label)').text())) : null,
        paidOn: paid ? parseDate(asOf) : null,
        delinquentAfter: paid ? null : parseDate(asOf),
      });
    });
    return installments;
  };

  const periods: TaxOwnershipPeriod[] = [];
  $('.collapsible').each((_, collapsible) => {
    const section = $(collapsible);
    const table = section.find('table.bills').first();
    if (table.length === 0) return;
    const label = text(section.find('button .link-text').first().text());
    if (!label) return;
    const bills = new Map<string, TaxBill>();
    let nestedUnder: string | null = null;
    table.find('tbody').each((_, body) => {
      const tbody = $(body);
      const billNumber = tbody.attr('data-bill-number');
      if (!billNumber) return;
      const classes = (tbody.attr('class') ?? '').split(/\s+/);
      if (classes.includes('installment')) {
        const bill = bills.get(billNumber);
        // Installments numbered like a defaulted item belong to the annual bill
        // nested beneath it, which the redemption amount already covers.
        if (!bill || bill.kind === 'defaulted') return;
        bill.installments.push(...parseInstallmentRows(tbody.find('tr.d-md-table-row')));
        return;
      }
      const link = tbody.find('a[href*="/bills/"]').first();
      const billLabel = text(link.text());
      // A defaulted-taxes item is numbered after its newest bill ("Default
      // #17-02704" carries the 2022 bill number), so its label never matches.
      const labelNumber = billNumberFrom(billLabel);
      if (!billLabel || (labelNumber !== null && labelNumber !== billNumber)) return;
      const detailUrl = absolute(link.attr('href'), accountUrl) ?? accountUrl;
      const printUrl = absolute(tbody.find('a[href$="/print"]').attr('href'), accountUrl);
      const frameUrl = frameUrlFor(link) ?? printUrl?.replace(/\/print$/, '') ?? detailUrl;
      const kind = classifyTaxBill(billLabel);
      const nested = classes.includes('nested');
      const bill: TaxBill = {
        billNumber,
        year: billYearFrom(billLabel),
        kind,
        label: billLabel,
        detailUrl,
        frameUrl,
        printUrl,
        paymentPlanBillNumber: nested ? nestedUnder : null,
        installments: [],
        detail: null,
      };
      // Payment plans and defaulted taxes carry their own balance as one
      // installment, with the bills they cover nested beneath.
      if (kind === 'paymentPlan' || kind === 'defaulted') {
        const row = tbody.find('tr.d-md-table-row').first();
        const balance = parseMoney(text(row.find('td.balance').text()));
        const statusLabel = text(row.find('td.status .label').text());
        if (balance !== null || statusLabel)
          bill.installments.push({
            label: kind === 'paymentPlan' ? 'Payment plan' : 'Redemption amount',
            amountDue: balance,
            status: installmentStatus(statusLabel.toLowerCase()),
            paidAmount: null,
            paidOn: null,
            delinquentAfter: parseDate(text(row.find('td.as-of').text())),
          });
        nestedUnder = billNumber;
      } else if (!nested) nestedUnder = null;
      bills.set(billNumber, bill);
    });
    periods.push({
      label,
      current: /^current owner/i.test(label),
      bills: [...bills.values()],
    });
  });
  const totalAmountDue = parseMoney(
    text(
      $('tfoot th:contains("Total amount due")').first().closest('tr').find('td.balance').text(),
    ),
  );
  const account = { accountNumber, address, accountUrl, totalAmountDue, periods };
  // The table shell can arrive before its desktop installment rows. A bill
  // header (or a readable detail page) does not prove payment data was loaded.
  if (
    !periods.length ||
    periods.some((period) => !period.bills.length) ||
    periods.flatMap((period) => period.bills).some((bill) => !hasTaxBillListing(bill))
  )
    throw new TreasurerParseError('Treasurer account installment listing is incomplete.');
  return account;
}

/** Nested bills are covered by the plan/redemption item, rather than separate payments. */
export function hasTaxBillListing(bill: TaxBill) {
  if (bill.paymentPlanBillNumber) return true;
  const expected = ['annual', 'supplemental', 'escape'].includes(bill.kind) ? 2 : 1;
  return (
    bill.installments.length >= expected &&
    new Set(bill.installments.map((item) => item.label)).size === bill.installments.length &&
    bill.installments.every(
      (item) =>
        item.status !== 'unknown' &&
        item.amountDue !== null &&
        (item.status === 'paid' ? item.amountDue === 0 : item.delinquentAfter !== null),
    )
  );
}

function readValues(
  section: Record<string, string> | undefined,
  keys: { land: string; improvements: string; personal: string },
): TaxAssessedValues | null {
  if (!section) return null;
  const pick = (label: string) => {
    const entry = Object.entries(section).find(
      ([key]) => key.toLowerCase() === label.toLowerCase(),
    );
    return parseMoney(entry?.[1]);
  };
  const values = {
    land: pick(keys.land),
    improvements: pick(keys.improvements),
    personalProperty: pick(keys.personal),
    exemptions: pick('other exemption'),
    net: pick('total taxable value'),
  };
  return Object.values(values).some((value) => value !== null) ? values : null;
}

/** Parses a bill detail document (`/iframe-taxsys/.../bills/<id>`). */
export function parseTreasurerBillPage(html: string): TaxBillDetail {
  const $ = load(html);
  const sections: Record<string, Record<string, string>> = {};
  $('.row.header').each((_, header) => {
    const name = text($(header).text()).replace(/:$/, '');
    if (!name) return;
    const entries: Record<string, string> = {};
    let row = $(header).next();
    while (row.length && !row.hasClass('header')) {
      const label = text(row.find('.label').first().text()).replace(/:$/, '');
      const value = text(row.find('.value').first().text());
      if (label) entries[label] = value;
      row = row.next();
    }
    sections[name] = entries;
  });
  const heading = $('h2:contains("Details:")').first();
  if (heading.length === 0 && Object.keys(sections).length === 0)
    throw new TreasurerParseError('Treasurer bill page has no bill details.');
  const fiscal = /Fiscal Year:\s*([\d-]+)/.exec(text($('.fiscal-year').text()));
  const assessedRow = $('table.assessed-values-table tbody tr').first();
  const assessedCells = assessedRow
    .find('td')
    .map((_, cell) => text($(cell).text()))
    .get();
  const assessedHeaders = $('table.assessed-values-table thead th')
    .map((_, cell) => text($(cell).text()).toLowerCase())
    .get();
  const assessedValue = (name: string) => {
    const index = assessedHeaders.indexOf(name);
    return index === -1 ? null : parseMoney(assessedCells[index]);
  };
  const assessed: TaxAssessedValues | null =
    assessedCells.length > 0
      ? {
          land: assessedValue('land'),
          improvements: assessedValue('improvements'),
          personalProperty: assessedValue('personal property'),
          exemptions: assessedValue('exemptions'),
          net: assessedValue('net assessed value'),
        }
      : null;
  const find = (section: RegExp) =>
    Object.entries(sections).find(([name]) => section.test(name))?.[1];
  const info = find(/^bill information$/i) ?? {};
  const general = find(/^general$/i) ?? {};
  const field = (source: Record<string, string>, label: RegExp) =>
    Object.entries(source).find(([key]) => label.test(key))?.[1] ?? null;
  const componentKeys = {
    land: 'Land',
    improvements: 'Improvements',
    personal: 'Personal Property',
  };
  return {
    paymentSchedule: parsePaymentSchedule($),
    fiscalYear: fiscal?.[1] ?? null,
    assessed,
    prior: readValues(find(/^prior assessment values$/i), componentKeys),
    change: readValues(find(/^change in assessed values$/i), componentKeys),
    assessmentYear: field(info, /^assessment year$/i),
    assessmentEvent: field(info, /^assessment event$/i),
    effectiveDate: field(info, /^effective date$/i),
    billablePeriod: field(info, /^billable period$/i),
    issuedOn: parseDate(field(info, /issued\/mailed date$/i)),
    totalTax: parseMoney(field(info, /^total tax$/i)),
    taxRate: field(general, /^tax rate$/i),
    daysOfOwnership: field(general, /^days of ownership$/i),
    sections,
  };
}

/** The plan balance's "valid through" date is not an installment deadline. */
export function taxBillInstallments(bill: TaxBill) {
  if (bill.paymentPlanBillNumber) return [];
  if (bill.kind === 'paymentPlan') return bill.detail?.paymentSchedule ?? [];
  return bill.installments;
}

function parsePaymentSchedule($: CheerioAPI): TaxInstallment[] {
  const schedule: TaxInstallment[] = [];
  $('table').each((_, table) => {
    const headers = $(table)
      .find('th')
      .map((_, th) => text($(th).text()).toLowerCase())
      .get();
    const amount = headers.indexOf('amount');
    const status = headers.indexOf('status');
    const date = headers.findIndex((h) => /date due\s*\/\s*paid/.test(h));
    if (amount < 0 || status < 0 || date < 0) return;
    $(table)
      .find('tbody tr')
      .each((_, tr) => {
        const cells = $(tr)
          .find('td')
          .map((_, td) => text($(td).text()))
          .get();
        if (!cells.length) return;
        const state = installmentStatus((cells[status] ?? '').toLowerCase());
        const value = parseMoney((cells[amount] ?? '').replace(/\*/g, ''));
        const day = parseDate(cells[date] ?? null);
        if ((cells[status] ?? '').toLowerCase() === 'paid' && (value === null || !day))
          throw new TreasurerParseError('Treasurer payment plan details are incomplete.');
        schedule.push({
          label: cells[0] || `Payment ${schedule.length + 1}`,
          status: state,
          amountDue: state === 'paid' ? 0 : value,
          paidAmount: state === 'paid' ? value : null,
          paidOn: state === 'paid' ? day : null,
          delinquentAfter: state === 'paid' ? null : day,
        });
      });
  });
  return schedule;
}

/**
 * A browser session that has already cleared the portal's challenge. Adapters
 * (Browserbase, a local Chromium, a fixture replay) implement this; core code
 * never depends on a browser runtime.
 */
export interface TreasurerBrowserSession {
  /** Navigates the top-level document and resolves the settled outer HTML. */
  loadDocument(
    url: string,
    options: { readyWhen: RegExp; isReady?: (html: string) => boolean },
  ): Promise<string>;
  /** Parallel-safe direct read with serialized navigation fallback. */
  readDocument?(
    url: string,
    options: { readyWhen: RegExp; validate: (html: string) => boolean },
  ): Promise<string>;
  /** Resolves the current top-level document URL after any redirects. */
  currentUrl(): Promise<string>;
  /** Waits for an iframe `src` in the current document that matches, if any. */
  findFrameUrl(pattern: RegExp, options?: { waitForAccount?: boolean }): Promise<string | null>;
  /** Selects exactly one matching account from a settled official search, or throws. */
  selectAccount?(parcelNumber: string): Promise<void>;
  /** Downloads a same-origin URL from inside the page (session cookies apply). */
  download(
    url: string,
  ): Promise<{ contentType: string; bytes: Uint8Array; providerStatus?: number }>;
  close(): Promise<void>;
}

export type TreasurerSourceOptions = {
  openSession: () => Promise<TreasurerBrowserSession>;
  /** Upper bound on bill detail pages loaded per snapshot (browser time is metered). */
  maxDetailPages?: number;
  now?: () => Date;
};

const IFRAME_PATTERN = /\/iframe-taxsys\/.*\/property-tax\//;
const ACCOUNT_READY = /table[^>]*class="[^"]*\bbills\b|No results|not be found/i;
const BILL_READY = /Details:|Bill Information|not be found/i;
const TREASURER_HOSTS = ['county-taxes.net', 'county-taxes.com'];

function isTreasurerHost(url: URL) {
  return (
    url.protocol === 'https:' &&
    TREASURER_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))
  );
}

export function normalizeParcelNumber(block: string, lot: string) {
  return `${block}${lot}`.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** Only the official SF search routes, for this exact query, may select an account. */
export function isTreasurerSearchUrl(value: string, parcelNumber?: string) {
  if (!URL.canParse(value)) return false;
  const url = new URL(value);
  const query = url.searchParams.get('search_query');
  return (
    url.protocol === 'https:' &&
    !url.username &&
    !url.password &&
    !url.port &&
    !url.hash &&
    ((url.hostname === 'sanfrancisco-ca.county-taxes.com' &&
      url.pathname === '/public/search/gsgx_property_tax') ||
      (url.hostname === 'county-taxes.net' && url.pathname === '/ca-sanfrancisco/property-tax')) &&
    [...url.searchParams.keys()].length === 1 &&
    !!query &&
    /^[0-9A-Z-]+$/i.test(query) &&
    (!parcelNumber || normalizeParcelNumber(query, '') === parcelNumber)
  );
}

/** The portal can auto-select a single search result. Identity is still checked in the account document. */
export function isTreasurerAccountUrl(value: string) {
  if (!URL.canParse(value)) return false;
  const url = new URL(value);
  return (
    url.protocol === 'https:' &&
    url.hostname === 'county-taxes.net' &&
    !url.username &&
    !url.password &&
    !url.port &&
    !url.search &&
    !url.hash &&
    /^\/ca-sanfrancisco\/property-tax\/[A-Za-z0-9_-]+={0,2}$/.test(url.pathname)
  );
}

/** A missing exact result is still unresolved; it is never evidence of no tax bills. */
export function treasurerSearchAccount(html: string, url: string, parcelNumber: string) {
  if (!isTreasurerSearchUrl(url, parcelNumber))
    throw new TreasurerParseError('Treasurer search URL did not match the requested parcel.');
  const $ = load(html);
  const headings = $('h5')
    .toArray()
    .map((h) => text($(h).text()));
  const pages = headings
    .map((h) => /^Search Results\s*-\s*Page (\d+) of (\d+)$/i.exec(h))
    .filter(Boolean);
  if (pages.length !== 1) return null;
  if (pages[0]![1] !== '1' || pages[0]![2] !== '1')
    throw new TreasurerParseError('Treasurer search results span multiple pages.');
  const matches = $('.property-tax.row')
    .toArray()
    .filter((card) => {
      // The suffix can sit outside <mark>; the complete account heading is authoritative.
      const heading = text($(card).find('.row.font-weight-semibold .col-12').first().text());
      const account = /^Account\s+(\d{4}[A-Z]?-\d{3}[A-Z]?)$/i.exec(heading)?.[1];
      return account && normalizeParcelNumber(account, '') === parcelNumber;
    });
  if (matches.length > 1)
    throw new TreasurerParseError('Treasurer search returned ambiguous exact accounts.');
  if (!matches.length) return null;
  const buttons = $(matches[0]!).find('button[data-test="view"]');
  if (buttons.length !== 1 || buttons.is('[disabled]')) return null;
  return parcelNumber;
}

export function createTreasurerSource(options: TreasurerSourceOptions) {
  const maxDetailPages = options.maxDetailPages ?? 12;
  const now = options.now ?? (() => new Date());

  async function fetchTaxBills(
    block: string,
    lot: string,
    detail: {
      billNumbers?: string[];
      /** Skips detail pages the caller already holds; defaults to every bill except defaulted redemption balances. */
      shouldLoadDetail?: (bill: TaxBill) => boolean;
    } = {},
  ): Promise<TaxBillSnapshot> {
    const parcelNumber = normalizeParcelNumber(block, lot);
    const sourceUrl = treasurerSourceUrl(parcelNumber);
    const session = await options.openSession();
    try {
      await session.loadDocument(sourceUrl, { readyWhen: /./ });
      let frameUrl = await session.findFrameUrl(IFRAME_PATTERN);
      let landed = await session.currentUrl();
      if (!frameUrl && isTreasurerSearchUrl(landed, parcelNumber)) {
        if (!session.selectAccount)
          throw new TreasurerParseError('Treasurer search did not resolve an exact account.');
        await session.selectAccount(parcelNumber);
        frameUrl = await session.findFrameUrl(IFRAME_PATTERN, { waitForAccount: true });
        landed = await session.currentUrl();
      }
      if (
        !frameUrl &&
        !isTreasurerSearchUrl(landed) &&
        URL.canParse(landed) &&
        isTreasurerHost(new URL(landed))
      ) {
        // The search redirect lands on the GovHub shell, which sometimes stalls
        // on "Loading"; reloading the landed URL renders the account frame.
        await session.loadDocument(landed, { readyWhen: /./ });
        frameUrl = await session.findFrameUrl(IFRAME_PATTERN);
        landed = await session.currentUrl();
      }
      if (!frameUrl) {
        throw new TreasurerParseError('Treasurer portal did not render an account frame.');
      }
      const accountUrl = frameUrl;
      if (!URL.canParse(accountUrl) || !isTreasurerHost(new URL(accountUrl)))
        throw new TreasurerParseError('Treasurer account frame is not an approved host.');
      const accountHtml = await session.loadDocument(accountUrl, {
        readyWhen: ACCOUNT_READY,
        isReady: (html) => {
          try {
            return parseTreasurerAccountPage(html, accountUrl) !== null;
          } catch {
            return false;
          }
        },
      });
      const account = parseTreasurerAccountPage(accountHtml, accountUrl);
      if (!account || normalizeParcelNumber(account.accountNumber, '') !== parcelNumber)
        throw new TreasurerParseError('Treasurer account did not match the requested parcel.');
      const detailFailures: string[] = [];
      let pendingDetailBillNumbers: string[] = [];
      if (account) {
        const wanted = new Set(detail.billNumbers ?? []);
        const bills = account.periods.flatMap((period) => period.bills);
        const requested = (
          detail.billNumbers
            ? bills.filter((bill) => wanted.has(bill.billNumber))
            : bills.filter((bill) => bill.kind !== 'defaulted')
        ).filter((bill) => detail.shouldLoadDetail?.(bill) ?? true);
        const targets = requested.slice(0, maxDetailPages);
        pendingDetailBillNumbers = requested.slice(maxDetailPages).map((bill) => bill.billNumber);
        let next = 0;
        const failed = new Set<string>();
        const worker = async () => {
          while (next < targets.length) {
            const bill = targets[next++]!;
            try {
              const html = await (session.readDocument ?? session.loadDocument).call(
                session,
                bill.frameUrl,
                {
                  readyWhen: BILL_READY,
                  validate: (html: string) => {
                    const detail = parseTreasurerBillPage(html);
                    return (
                      detail.sections['Bill Information']?.['Bill #'] === bill.billNumber &&
                      normalizeParcelNumber(
                        detail.sections.General?.['Account Number'] ?? '',
                        '',
                      ) === parcelNumber
                    );
                  },
                },
              );
              bill.detail = parseTreasurerBillPage(html);
            } catch {
              failed.add(bill.billNumber);
            }
          }
        };
        await Promise.all(Array.from({ length: session.readDocument ? 2 : 1 }, worker));
        detailFailures.push(
          ...targets.filter((bill) => failed.has(bill.billNumber)).map((bill) => bill.billNumber),
        );
      }
      return {
        parserVersion: TREASURER_PARSER_VERSION,
        parcelNumber,
        fetchedAt: now().toISOString(),
        sourceUrl,
        account,
        detailFailures,
        pendingDetailBillNumbers,
      };
    } finally {
      await session.close().catch(() => undefined);
    }
  }

  /** Downloads the official bill PDF; the print URL comes from a prior snapshot. */
  async function downloadBillPdf(bill: Pick<TaxBill, 'printUrl' | 'frameUrl'>) {
    if (!bill.printUrl) throw new TreasurerParseError('Bill has no PDF.');
    const session = await options.openSession();
    try {
      // The print endpoint needs the portal's own cookies, so load the bill page first.
      const origin = new URL(bill.printUrl);
      if (!isTreasurerHost(origin) || !isTreasurerHost(new URL(bill.frameUrl)))
        throw new TreasurerParseError(
          `Refusing to download from ${origin.host}: not a Treasurer host.`,
        );
      await session.loadDocument(bill.frameUrl, { readyWhen: BILL_READY });
      const file = await session.download(bill.printUrl);
      if (!/application\/pdf/i.test(file.contentType) || file.bytes.length < 4)
        throw new TreasurerParseError(
          `Treasurer returned ${file.contentType || 'no content type'} from ${origin.host} instead of a PDF.`,
          file.providerStatus,
        );
      return file.bytes;
    } finally {
      await session.close().catch(() => undefined);
    }
  }

  /** Some zero-dollar bills have a detail page but no official PDF. Archive that page. */
  async function downloadBillPage(
    bill: Pick<TaxBill, 'frameUrl' | 'billNumber'>,
    parcelNumber: string,
  ) {
    const url = new URL(bill.frameUrl);
    if (
      !isTreasurerHost(url) ||
      url.username ||
      url.password ||
      url.port ||
      !/\/property-tax\/[^/]+\/bills\/[^/]+$/.test(url.pathname) ||
      !/^\d{4,20}$/.test(bill.billNumber) ||
      !/^[0-9A-Z]{7,10}$/.test(parcelNumber)
    )
      throw new TreasurerParseError('Invalid bill page identity.');
    const session = await options.openSession();
    try {
      const html = await session.loadDocument(url.href, { readyWhen: BILL_READY });
      const finalUrl = new URL(await session.currentUrl());
      if (
        finalUrl.origin !== url.origin ||
        finalUrl.pathname !== url.pathname ||
        html.length > 2_000_000
      )
        throw new TreasurerParseError('Bill page changed location or exceeds the archive limit.');
      const detail = parseTreasurerBillPage(html);
      if (
        detail.sections['Bill Information']?.['Bill #'] !== bill.billNumber ||
        normalizeParcelNumber(detail.sections.General?.['Account Number'] ?? '', '') !==
          parcelNumber
      )
        throw new TreasurerParseError('Bill page does not match the requested bill and parcel.');
      const $ = load(html);
      // Store only the readable source page, never session scripts or form credentials.
      $(
        'script,noscript,svg,math,iframe,object,embed,base,meta[http-equiv],link,input,textarea,select,button,.modals',
      ).remove();
      $('form').each((_, form) => {
        $(form).replaceWith($(form).contents());
      });
      $('*').each((_, element) => {
        for (const name of Object.keys('attribs' in element ? element.attribs : {}))
          if (/^on/i.test(name) || ['srcdoc', 'srcset', 'action', 'formaction'].includes(name))
            $(element).removeAttr(name);
      });
      $('a').removeAttr('href');
      $('img').each((_, image) => {
        if (!/^data:image\/(png|jpeg|gif|webp);base64,/i.test($(image).attr('src') ?? ''))
          $(image).remove();
      });
      const notice = $('<aside>').text(
        `Archived bill page · official PDF not provided · Captured ${now().toISOString()} · Source: ${url.href}`,
      );
      $('body').prepend(notice);
      return new TextEncoder().encode($.html());
    } finally {
      await session.close().catch(() => undefined);
    }
  }

  return { fetchTaxBills, downloadBillPdf, downloadBillPage };
}

/** Flattens a snapshot into bills newest-first, current owner first. */
export function listTaxBills(snapshot: TaxBillSnapshot | null | undefined) {
  if (!snapshot?.account) return [];
  return snapshot.account.periods.flatMap((period) =>
    period.bills.map((bill) => ({ ...bill, period })),
  );
}
